import { Fragment, type ComponentPropsWithRef, type ReactNode } from "react";

import { cn } from "../lib/cn";

export interface BreadcrumbItem {
  label: ReactNode;
  /** Absent sur le dernier élément : la page courante n'est pas un lien. */
  href?: string;
}

export interface BreadcrumbProps extends Omit<ComponentPropsWithRef<"nav">, "children"> {
  items: BreadcrumbItem[];
  separator?: ReactNode;
}

/**
 * Fil d'Ariane.
 *
 * Le dernier élément est rendu en `<span aria-current="page">` et jamais en
 * lien : proposer de naviguer vers la page où l'on se trouve déjà est une
 * fausse affordance, et les lecteurs d'écran l'annoncent alors correctement.
 */
export function Breadcrumb({
  className,
  items,
  separator = "/",
  ...props
}: BreadcrumbProps) {
  return (
    <nav aria-label="Fil d'Ariane" className={className} {...props}>
      <ol className="flex flex-wrap items-center gap-1.5 text-sm">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          return (
            <Fragment key={index}>
              <li>
                {isLast || !item.href ? (
                  <span
                    aria-current={isLast ? "page" : undefined}
                    className={cn(isLast ? "font-medium text-fg" : "text-fg-muted")}
                  >
                    {item.label}
                  </span>
                ) : (
                  <a
                    href={item.href}
                    className="rounded-sm text-fg-muted transition-colors hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  >
                    {item.label}
                  </a>
                )}
              </li>

              {!isLast && (
                <li aria-hidden="true" className="text-fg-subtle select-none">
                  {separator}
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
